import React, { useContext, useEffect, useState } from "react";
import { LoanCalculatorWrapper } from "./style";
import img from "../../images/loan_calculation.png";
import RangeSlider from "../RangeSlider/RangeSlider";
import principalIcon from '../../images/Principal.svg';
import emiIcon from '../../images/EMI.svg';
import paybleIcon from '../../images/Total payble.svg';
import { getStorage, setStorage } from "../../Utils/common";
import Button from "../ui/Button";
import ContextDashboard from "../../Context/ContextDashboard";
import { calculateLoan, generateLoan } from "../../Utils/api";
import { useNavigate } from "react-router-dom";
import Select from '../../components/ui/Select';
import ProgressBar from "../../components/ProgressBar/ProgressBar";

const initialData = {
  loanPurpose: ""
};

const options = [
  { label: "Travel", value: "1" },
  { label: "Medical", value: "2" }, 
  { label: "Shopping", value: "3" },
  { label: "Others", value: "4" },
];

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString("en-IN");

function LoanCalculator() {
  const [principal, setPrincipal] = useState(null);
  const [tenure, setTenure] = useState(null);
  const [emi, setEmi] = useState(0);
  const [loadingAccept, setLoadingAccept] = useState(false);  // Loading state for Accept button
  const [loadingReject, setLoadingReject] = useState(false);  // Loading state for Reject button
  const [formData, setFormData] = useState(initialData);
  const [formDataError, setFormDataError] = useState(initialData);
  const [progressBar, setProgressBar] = useState(getStorage('percent'));
  const [loanData, setLoanData] = useState({});
    
  const { setMessage, logout, handleEvent } = useContext(ContextDashboard);

  useEffect(() => {
    const loadQuote = async () => {
      const params = {
        profile_id: getStorage("cust_profile_id") || "",
        event_name: "generate_loan_quote",
      };
      generateLoan(params).then(resp => {
      if (resp?.data?.Status === 1) {
        const data = resp?.data?.Data || {};
        setStorage('percent', data?.step_percentage);
        setProgressBar(data?.step_percentage);
        setLoanData(data);

        // Default to max eligible amount so customers see full reloan eligibility,
        // not the hardcoded ₹5,000 floor that was causing confusion.
        const minAmount = Number(data?.min_loan_amount) || 5000;
        const maxAmount = Number(data?.max_loan_amount) || minAmount;
        setPrincipal(maxAmount);

        const minTenure = Number(data?.min_loan_tenure) || 7;
        const maxTenure = Number(data?.max_loan_tenure) || minTenure;
        setTenure(maxTenure);
      } else if (resp?.data?.Status === 4) {
        logout();
      } else {
        setMessage({
          type: "error",
          msg: resp?.data?.Message || "Unable to calculate your loan eligibility. Please try again.",
        });
      }
    }).catch(() => {
        setMessage({
          type: "error",
          msg: "Unable to calculate your loan eligibility. Please try again.",
        });
      });
    };

    loadQuote();
  }, []);

  const submit = (acceptedId) => {
    const param = {
      profile_id: getStorage("cust_profile_id") || "", 
      event_name: "loan_quotation_decision",
      tenure: tenure,
      purpose_id: formData.loanPurpose,
      amount: principal,
      accepted_id: acceptedId // Pass the accepted_id here
    };

    // Set loading state depending on whether the user clicked "Accept" or "Reject"
    if (acceptedId === "1") {
      setLoadingAccept(true); // Start loading for "Accept"
      setLoadingReject(false); // Ensure "Reject" is not loading
    } else if (acceptedId === "2") {
      setLoadingReject(true); // Start loading for "Reject"
      setLoadingAccept(false); // Ensure "Accept" is not loading
    }

    calculateLoan(param).then(resp => {
      // Reset the loading state after the request completes
      if (acceptedId === "1") setLoadingAccept(false); // Stop loading for "Accept"
      if (acceptedId === "2") setLoadingReject(false); // Stop loading for "Reject"

      if (resp?.data?.Status === 1) {
        setStorage("next_step", resp?.data?.Data?.next_step);
        setStorage('percent', resp?.data?.Data?.step_percentage);
        setProgressBar(resp?.data?.Data?.step_percentage);
        setMessage({ type: 'success', msg: resp?.data?.Message, place: "global" });
        handleEvent(getStorage('next_step'));
      } else if (resp?.data?.Status === 4) {
        logout();
      } else {
        setMessage({ type: 'error', msg: resp?.data?.Message });
      }
    });
  };

  const onChange = (e) => {
    let { name, value } = e.target;  
    setFormData({ ...formData, [name]: value });
    setFormDataError({ ...formDataError, [name]: "" });
  };

  useEffect(() => {
    if (principal == null || tenure == null) {
      setEmi(0);
      return;
    }
    let si = (principal * tenure) / 100;
    setEmi(si);
  }, [principal, tenure]);

  const minLoanAmount = Number(loanData?.min_loan_amount) || 5000;
  const maxLoanAmount = Number(loanData?.max_loan_amount) || minLoanAmount;
  const quoteReady = principal != null && tenure != null;

  return (
    <>
    
      <LoanCalculatorWrapper className="flex">
        <div className="left">
          <div>
            <p>
              WE HAVE CALCULATED YOUR LOAN ELIGIBILITY BASED ON THE GIVEN DETAILS
              PLEASE CHOOSE THE LOAN AMOUNT AND TENURE AS PER YOUR REQUIREMENTS.
            </p>
          </div>
        </div>
        <div className="right">
          <div className="box">
            <div className="flex space-between">
              <h2>Purpose of Loan <span style={{color: "red"}}>*</span></h2>
            </div>
            <div className="center">
              <Select
                className="selectLoanPurpose"
                name="loanPurpose"
                placeholder="--select--"
                error={formDataError?.loanPurpose}
                onChange={onChange}
                value={formData.loanPurpose} 
                options={options}
              />
            </div>
          </div>
          <div className="box">
            <div className="flex space-between">
              <h2>Principal <span style={{color: "red"}}>*</span></h2>
              <span className="value">₹ {quoteReady ? formatAmount(principal) : "—"}</span>
            </div>
            {quoteReady && (
              <p className="eligible-range">
                You are eligible for ₹ {formatAmount(minLoanAmount)} – ₹ {formatAmount(maxLoanAmount)}. Use the slider to choose your amount.
              </p>
            )}
            <div className="center">
              {quoteReady && (
                <RangeSlider
                  min={minLoanAmount}
                  step="500"
                  max={maxLoanAmount}
                  value={principal ?? maxLoanAmount}
                  setValue={setPrincipal}
                />
              )}
            </div>
          </div>
          <div className="box">
            <div className="flex space-between">
              <h2>Tenure <span style={{color: "red"}}>*</span></h2>
              <span className="value">{quoteReady ? `Tenure fixed for ${tenure} Days` : "—"}</span>
            </div>
            <div className="center">
              {quoteReady && (
                <RangeSlider
                  min={Number(loanData?.min_loan_tenure) || 7}
                  step="1"
                  max={Number(loanData?.max_loan_tenure) || 40}
                  value={tenure ?? (Number(loanData?.max_loan_tenure) || 40)}
                  setValue={setTenure}
                />
              )}
            </div>
          </div>
          <div className="cards">
            <div className="card">
              <div className="img">
                <img src={principalIcon} alt="icon" />
              </div>
              <div className="desc">
                <h3>PRINCIPAL</h3>
                <h2>₹ {quoteReady ? formatAmount(principal) : "—"}</h2>
              </div>
            </div>
            <div className="card">
              <div className="img">
                <img src={paybleIcon} alt="icon" />
              </div>
              <div className="desc">
                <h3>TOTAL PAYABLE</h3>
                <h2>₹ {quoteReady ? formatAmount(Number(principal) + Number(emi)) : "—"}</h2>
              </div>
            </div>
          </div>
          <Button title={"Accept"} onClick={() => submit("1")} loading={loadingAccept} disabled={!quoteReady} />
          {/* <Button title={"Reject"} onClick={() => submit("2")} loading={loadingReject} /> */}
        </div>
      </LoanCalculatorWrapper>
    </>
  );
}

export default LoanCalculator;
